import React from 'react';
import { Link } from 'react-router-dom';
import { Search, FileText, Code, Image, Tag } from 'lucide-react';

interface SearchResult {
  id: string;
  title: string;
  description: string;
  type: 'demo' | 'blog' | 'day' | 'page';
  url: string;
  tags?: string[];
  date?: string;
}

interface SearchResultsProps {
  results: SearchResult[];
  query: string;
  isLoading?: boolean;
  onResultClick?: (result: SearchResult) => void;
  className?: string;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  query,
  isLoading = false,
  onResultClick,
  className = ""
}) => {
  // 結果タイプに対応するアイコンを取得
  const getIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'demo':
        return <Code className="w-5 h-5" />;
      case 'blog':
        return <FileText className="w-5 h-5" />;
      case 'day':
        return <Image className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  // 結果タイプに対応するラベルを取得
  const getTypeLabel = (type: SearchResult['type']) => {
    switch (type) {
      case 'demo':
        return 'デモ';
      case 'blog':
        return 'ブログ';
      case 'day':
        return 'チャレンジ';
      default:
        return 'ページ';
    }
  };

  // クエリをハイライト
  const highlightQuery = (text: string, query: string) => {
    if (!query) return text;
    
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className="bg-yellow-400 text-black px-1 rounded">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  if (isLoading) {
    return (
      <div className={`bg-black/80 backdrop-blur-sm border border-white/10 p-6 ${className}`}>
        <div className="space-y-4">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="animate-pulse flex space-x-4">
              <div className="rounded-full bg-white/10 h-10 w-10"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-white/10 rounded w-3/4"></div>
                <div className="h-3 bg-white/10 rounded"></div>
                <div className="h-3 bg-white/10 rounded w-5/6"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!query) {
    return (
      <div className={`bg-black/80 backdrop-blur-sm border border-white/10 p-6 text-center ${className}`}>
        <Search className="w-12 h-12 text-white/30 mx-auto mb-4" />
        <p className="text-white/50">検索クエリを入力してください</p>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className={`bg-black/80 backdrop-blur-sm border border-white/10 p-6 text-center ${className}`}>
        <Search className="w-12 h-12 text-white/30 mx-auto mb-4" />
        <p className="text-white/50">
          &quot;{query}&quot; に一致する結果が見つかりませんでした
        </p>
        <p className="text-white/30 text-sm mt-2">
          別のキーワードをお試しください
        </p>
      </div>
    );
  }

  return (
    <div className={`bg-black/80 backdrop-blur-sm border border-white/10 overflow-hidden ${className}`}>
      <div className="p-4 border-b border-white/10">
        <p className="text-white/50 text-sm">
          {results.length} 件の結果
        </p>
      </div>

      <div className="divide-y divide-white/10 max-h-96 overflow-y-auto">
        {results.map((result) => (
          <Link
            key={result.id}
            to={result.url}
            onClick={() => onResultClick?.(result)}
            className="block p-4 hover:bg-white/5 transition-colors group"
          >
            <div className="flex items-start space-x-3">
              <div className="mt-1 text-white/50">
                {getIcon(result.type)}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-xs font-medium px-2 py-1 bg-white/[0.06] text-white/60 rounded-full">
                    {getTypeLabel(result.type)}
                  </span>
                  {result.date && (
                    <span className="text-xs text-white/30">
                      {result.date}
                    </span>
                  )}
                </div>

                <h3 className="font-medium text-white group-hover:text-white/70 transition-colors truncate">
                  {highlightQuery(result.title, query)}
                </h3>

                <p className="text-sm text-white/50 mt-1 line-clamp-2">
                  {highlightQuery(result.description, query)}
                </p>

                {result.tags && result.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {result.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center text-xs px-2 py-1 bg-white/[0.06] text-white/50 rounded-full"
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default SearchResults;